import type { BondOrder } from '../domain/editor';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject
} from 'react';
import { atomListFromTool } from '../domain/markushLayers';
import type { DrawingLine, NormalizedPoint } from '../model';
import type { DrawingAtom } from '@peruselab/structure-editor/domain/graphDrawing';
import type { EditorSelection, EditorTool } from '@peruselab/structure-editor/domain/editor';
import type { DraftLine } from '@peruselab/structure-editor/domain/drawing';
import { createCommittedLine, toSupportedPointerType } from '@peruselab/structure-editor/domain/drawing';
import {
  connectedBondAngle,
  distanceBetween,
  normalizedToPixel,
  pixelToNormalized,
  pointAtAngle,
  proposedBondEndpoints,
  type BondDrawingConfig,
  type PixelPoint
} from '@peruselab/structure-editor/domain/bonds';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import { createRingTemplatePlacement, type RingTemplate } from '@peruselab/structure-editor/domain/ringTemplates';
import { screenToNormalized, type ViewportTransform } from '@peruselab/structure-editor/domain/viewport';
import {
  CLICK_MOVEMENT_TOLERANCE_PX,
  MOUSE_SELECTION_DISTANCE_PX,
  TOUCH_SELECTION_DISTANCE_PX,
  nearestPoint,
  pointInPolygon,
  selectionAtPoint,
  uniqueEndpoints,
  usesTouchTarget
} from '@peruselab/structure-editor/domain/paperHitTesting';
import type { PanDrag } from './usePaperViewport';
import { NO_SNAP, snappedDragOffset, type SnapPoint } from '../domain/gridSnap';
import { ESCAPE_PRIORITY, useEscapeAction } from './escapeArbiter';
import type { RenderableAnnotation } from '../domain/annotationRendering';
import type { TextMeasure } from '../rendering/textLayout';
import { annotationExtentPoints } from '../rendering/annotationExtents';
import { LABEL_FONT_SIZE_PX, implicitHydrogenPosition } from '../chemistry/labelOutline';
import type { IonicPair } from '../domain/ionicPairs';

interface ActiveBondDraft extends DraftLine {
  readonly pointerStart: NormalizedPoint;
  readonly pointerCurrent: NormalizedPoint;
  readonly moved: boolean;
  readonly startedFromExistingEndpoint: boolean;
}

interface HydrogenDrag {
  readonly pointerId: number;
  readonly atomId: string;
  readonly direction: Readonly<{ x: number; y: number }>;
}

interface AtomDrag {
  readonly pointerId: number;
  readonly start: NormalizedPoint;
  readonly initialPositions: Readonly<Record<string, NormalizedPoint>>;
  readonly currentPositions: Readonly<Record<string, NormalizedPoint>>;
  readonly moved: boolean;
  // ADR 0014 -- set when the drag moves a whole reaction box (its molecules ride along).
  readonly reactionZone?: ReactionZoneId;
  /** Selection to narrow to if this press ends without moving (a 2nd click on a boxed molecule). */
  readonly narrowTo?: EditorSelection;
  /** The drag's current offset (paper-normalized) -- the box's preview and its final move. */
  readonly offset?: NormalizedPoint;
}

type ReactionZoneId = 'reactants' | 'products' | 'above' | 'below';

interface MarqueeDrag {
  readonly pointerId: number;
  readonly start: NormalizedPoint;
  readonly current: NormalizedPoint;
  readonly additive: boolean;
}

// #578: the freehand alternative to MarqueeDrag -- a running path of every point the pointer has
// visited, resolved to a selection via pointInPolygon at pointerUp instead of MarqueeDrag's AABB
// check.
interface LassoDrag {
  readonly pointerId: number;
  readonly points: readonly NormalizedPoint[];
  readonly additive: boolean;
}

// Shared by MarqueeDrag and LassoDrag resolution (#578): once a drag has decided which atoms it
// encloses, a bond joins the selection only when *both* its endpoint atoms are in that set --
// grabbing one end of a bond that reaches outside the drag shouldn't select it.
function bondIdsForSelectedAtoms(
  atoms: readonly DrawingAtom[],
  lines: readonly DrawingLine[],
  selectedAtomIds: ReadonlySet<string>
): readonly string[] {
  return lines
    .filter(
      (line) =>
        atoms.some(
          (atom) =>
            selectedAtomIds.has(atom.id) &&
            Math.abs(atom.position.x - line.start.x) < 1e-6 &&
            Math.abs(atom.position.y - line.start.y) < 1e-6
        ) &&
        atoms.some(
          (atom) =>
            selectedAtomIds.has(atom.id) &&
            Math.abs(atom.position.x - line.end.x) < 1e-6 &&
            Math.abs(atom.position.y - line.end.y) < 1e-6
        )
    )
    .map((line) => line.id);
}

interface ShapeDrag {
  readonly pointerId: number;
  readonly start: NormalizedPoint;
  readonly current: NormalizedPoint;
}

// The dragged item a drag snaps by: the initial position nearest where the press began.
function grabbedPoint(press: NormalizedPoint, positions: Readonly<Record<string, NormalizedPoint>>): NormalizedPoint {
  let best = press;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const point of Object.values(positions)) {
    const distance = Math.hypot(point.x - press.x, point.y - press.y);
    if (distance < bestDistance) {
      best = point;
      bestDistance = distance;
    }
  }
  return best;
}

export interface UsePaperPointerOptions {
  readonly tool: EditorTool;
  // #578 -- Edit tab "Lasso Selection" toggle. While true and `tool === 'select'`, a drag starting
  // on empty paper draws a freehand lasso path instead of the rectangular marquee.
  readonly lassoModeActive: boolean;
  readonly documentId?: string;
  // ADR 0014 -- the reaction zones (paper pixels): in Select mode, dragging inside a filled zone
  // (off any atom or bond) moves its molecules together.
  readonly reactionZones?: readonly {
    readonly zone: ReactionZoneId;
    readonly rect: { readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number };
    readonly empty: boolean;
    readonly atomIds: readonly string[];
  }[];
  /** A reaction box drag ended: move that box (and its molecules) by this paper-normalized offset. */
  readonly onReactionBoxMove?: (zone: ReactionZoneId, offset: NormalizedPoint) => void;
  /** ADR 0014 -- the molecule an atom belongs to when it sits in a reaction box (else undefined). */
  readonly boxedMolecule?: (atomId: string) => readonly string[] | undefined;
  readonly atoms: readonly DrawingAtom[];
  readonly lines: readonly DrawingLine[];
  readonly bondOrders: Readonly<Record<string, BondOrder>>;
  // ADR 0022 D2 -- the ionic bonds, so hovering near one shows its bolt.
  readonly ionicPairs?: readonly IonicPair[];
  // Order of the bond the armed rail tool will produce -- so a triple drawn off a single
  // neighbour goes straight (sp/linear), matching the "neighbour is a triple" case.
  readonly armedBondOrder: BondOrder;
  readonly bondConfig: BondDrawingConfig;
  readonly ringTemplate: RingTemplate | null;
  readonly selection: EditorSelection;
  // #637 -- annotations a box/lasso can enclose (text measured as on the paper).
  readonly annotations?: readonly RenderableAnnotation[];
  readonly measureText?: TextMeasure;
  readonly onSelectionChange: (selection: EditorSelection) => void;
  readonly onAtomChargeApply?: ((atomId: string) => void) | undefined;
  readonly onAtomElementChange: (atomId: string, atomicNumber: number) => void;
  // UI action rules Rule 4.4: an element armed + a click on empty paper places a lone atom there.
  readonly onAtomPlace: (point: NormalizedPoint, atomicNumber: number) => void;
  // #648 (Markush mode): the armed `r-group-${label}` tool clicked on an atom. Optional -- outside
  // Markush mode the tool is never armed.
  readonly onAtomRGroupLabel?: (atomId: string, label: string) => void;
  // #650: the same tool clicked on paper, not an atom -- inside a ring it adds an attachment point.
  readonly onPaperRGroupLabel?: (point: NormalizedPoint, label: string) => void;
  /** #736 -- a functional group (its key) on a clicked atom, or alone on empty paper. */
  readonly onAtomGroup?: (atomId: string, key: string) => void;
  readonly onPaperGroup?: (point: NormalizedPoint, key: string) => void;
  // #654: the armed `atom-list-${z,…}` tool clicked on an atom.
  readonly onAtomAtomList?: (atomId: string, allowedAtomicNumbers: readonly number[]) => void;
  // #656: a press on a repeat unit's count label (any tool) opens its count editor, at the
  // press's client point. `repeatUnitLabelAt` hit-tests paper pixels.
  readonly repeatUnitLabelAt?: (point: PixelPoint) => string | undefined;
  readonly onRepeatUnitActivate?: (unitId: string, clientPoint: Readonly<{ x: number; y: number }>) => void;
  // UI action rules Rule 3: a pointer-down in Draw mode nearer an existing bond's midpoint than
  // either endpoint applies the armed bond tool to it, rather than starting a new draft.
  readonly onBondToolApply: (bondId: string) => void;
  // #772 part 2b -- Snap to grid (identity when off); existing-atom and proposal snaps win.
  readonly snapPoint?: SnapPoint;
  readonly onImplicitHydrogenDirectionChange: (atomId: string, direction: Readonly<{ x: number; y: number }>) => void;
  readonly onAtomsMove: (positions: Readonly<Record<string, NormalizedPoint>>) => void;
  readonly onLinesChange: (lines: readonly DrawingLine[]) => void;
  readonly onRingPlace: (lines: readonly DrawingLine[], template: RingTemplate, sharedBondId?: string) => void;
  readonly onShapePlace: (
    shape: 'ellipse' | 'rect' | 'triangle' | 'star',
    start: NormalizedPoint,
    end: NormalizedPoint
  ) => void;
  readonly sizeRef: RefObject<PaperSize>;
  readonly linesRef: RefObject<readonly DrawingLine[]>;
  readonly viewportRef: RefObject<ViewportTransform>;
  readonly addBlinkMarker: (point: NormalizedPoint) => void;
  readonly panDrag: PanDrag | null;
  readonly beginPan: (pointerId: number, clientX: number, clientY: number) => void;
  readonly updatePan: (drag: PanDrag, clientX: number, clientY: number) => void;
  readonly endPan: () => void;
}

/**
 * Pointer lifecycle for DrawingPaper.tsx (item 33, step 3/4: see
 * docs/repo-topology-phase-5-drawing-paper-decomposition.md). Owns drag/draft state and the
 * three big pointer handlers; deliberately does NOT own the live-gesture-preview rendering memos
 * that read this state (ringPreviewLines, chainPreviewLines, svgDrafts, etc.) -- per the
 * characterization's Coupling 2, that dependency is nearly total, not partial, so those memos stay
 * in the component (or move in step 4 alongside the props-only rendering memos) rather than being
 * dragged into this hook just because they happen to read its output.
 */
/** Stable default, so the pointer-down handler isn't rebuilt every render without zones. */
const NO_REACTION_ZONES: NonNullable<UsePaperPointerOptions['reactionZones']> = [];

function distanceToSegment(point: PixelPoint, start: PixelPoint, end: PixelPoint): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (start.x + dx * t), point.y - (start.y + dy * t));
}

export function usePaperPointer({
  tool,
  lassoModeActive,
  documentId,
  reactionZones = NO_REACTION_ZONES,
  onReactionBoxMove,
  boxedMolecule,
  atoms,
  lines,
  bondOrders,
  ionicPairs,
  armedBondOrder,
  bondConfig,
  ringTemplate,
  selection,
  annotations = [],
  measureText,
  onSelectionChange,
  onAtomElementChange,
  onAtomChargeApply,
  onAtomPlace,
  onAtomRGroupLabel,
  onPaperRGroupLabel,
  onAtomGroup,
  onPaperGroup,
  onAtomAtomList,
  repeatUnitLabelAt,
  onRepeatUnitActivate,
  onBondToolApply,
  snapPoint = NO_SNAP,
  onImplicitHydrogenDirectionChange,
  onAtomsMove,
  onLinesChange,
  onRingPlace,
  onShapePlace,
  sizeRef,
  linesRef,
  viewportRef,
  addBlinkMarker,
  panDrag,
  beginPan,
  updatePan,
  endPan
}: UsePaperPointerOptions) {
  const activeDraftsRef = useRef<Map<number, ActiveBondDraft>>(new Map());
  const hydrogenDragRef = useRef<HydrogenDrag | null>(null);
  const [activeDrafts, setActiveDrafts] = useState<readonly ActiveBondDraft[]>([]);
  const [hoveredEndpoint, setHoveredEndpoint] = useState<PixelPoint | null>(null);
  const [hoveredIonicBondId, setHoveredIonicBondId] = useState<string | null>(null);
  // ADR 0020 D3 (#1099) -- the atom a mouse is on, in every mode, for its check mark's reason.
  const [hoveredAtomId, setHoveredAtomId] = useState<string | null>(null);
  const [hydrogenPreviewDirections, setHydrogenPreviewDirections] = useState<
    Readonly<Record<string, Readonly<{ x: number; y: number }>>>
  >({});
  const [atomDrag, setAtomDrag] = useState<AtomDrag | null>(null);
  const [marqueeDrag, setMarqueeDrag] = useState<MarqueeDrag | null>(null);
  const [lassoDrag, setLassoDrag] = useState<LassoDrag | null>(null);
  const [ringPointer, setRingPointer] = useState<NormalizedPoint | null>(null);
  const [shapeDrag, setShapeDrag] = useState<ShapeDrag | null>(null);

  const captureElementRef = useRef<HTMLDivElement | null>(null);
  const capturedIdsRef = useRef(new Set<number>());
  const cancel = useCallback(() => {
    activeDraftsRef.current.clear();
    hydrogenDragRef.current = null;
    setActiveDrafts([]);
    setAtomDrag(null);
    setMarqueeDrag(null);
    setLassoDrag(null);
    setShapeDrag(null);
    setHydrogenPreviewDirections({});
    setHoveredEndpoint(null);
    setRingPointer(null);
    for (const id of capturedIdsRef.current)
      if (captureElementRef.current?.hasPointerCapture(id)) captureElementRef.current.releasePointerCapture(id);
    capturedIdsRef.current.clear();
  }, []);
  useEffect(() => {
    // Committed geometry/tool changes invalidate native pointer capture and its preview.
    // eslint-disable-next-line react/set-state-in-effect
    cancel();
    return cancel;
  }, [tool, documentId, cancel]);
  // #762 -- Escape cancels an unfinished gesture (no Undo entry), and only then: an idle press
  // falls through to menus and the selection (escapeArbiter).
  useEscapeAction(ESCAPE_PRIORITY.gesture, () => {
    const active =
      activeDraftsRef.current.size > 0 ||
      hydrogenDragRef.current !== null ||
      atomDrag !== null ||
      marqueeDrag !== null ||
      lassoDrag !== null ||
      shapeDrag !== null ||
      panDrag !== null;
    if (!active) return false;
    cancel();
    if (panDrag) endPan();
    return true;
  });

  const previousGeometryRef = useRef({ atoms, lines });
  useEffect(() => {
    const previous = previousGeometryRef.current;
    previousGeometryRef.current = { atoms, lines };
    // Another pointer may add a bond while a draft is active. Preserve those drafts; cancel
    // only when existing geometry is removed or moved (undo, clear, replace, external edits).
    const changed =
      previous.atoms.some(
        (a) =>
          !atoms.some(
            (next) => next.id === a.id && next.position.x === a.position.x && next.position.y === a.position.y
          )
      ) || previous.lines.some((l) => !lines.some((next) => next.id === l.id));
    if (changed) cancel();
  }, [atoms, lines, cancel]);

  const syncDraftState = useCallback(() => {
    setActiveDrafts(Array.from(activeDraftsRef.current.values()));
  }, []);

  const normalizedPointForEvent = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      return screenToNormalized(
        { x: event.clientX - rect.left, y: event.clientY - rect.top },
        viewportRef.current,
        sizeRef.current
      );
    },
    [sizeRef, viewportRef]
  );

  const commitDraft = useCallback(
    (draft: DraftLine, blink: boolean) => {
      const line = createCommittedLine(draft);
      const nextLines = [...linesRef.current, line];
      linesRef.current = nextLines;
      onLinesChange(nextLines);
      if (blink) addBlinkMarker(line.end);
    },
    [addBlinkMarker, linesRef, onLinesChange]
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.pointerType === 'mouse' && event.button !== 0 && event.button !== 1) return;
      event.preventDefault();
      captureElementRef.current = event.currentTarget;
      capturedIdsRef.current.add(event.pointerId);
      if (tool === 'pan' || (event.pointerType === 'mouse' && event.button === 1)) {
        beginPan(event.pointerId, event.clientX, event.clientY);
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
      const repeatUnitId = repeatUnitLabelAt?.(normalizedToPixel(normalizedPointForEvent(event), sizeRef.current));
      if (repeatUnitId && onRepeatUnitActivate) {
        onRepeatUnitActivate(repeatUnitId, { x: event.clientX, y: event.clientY });
        return;
      }
      if (ringTemplate) {
        const placement = createRingTemplatePlacement(
          normalizedPointForEvent(event),
          ringTemplate,
          bondConfig.lengthPx,
          sizeRef.current,
          atoms,
          linesRef.current
        );
        onRingPlace(placement.lines, ringTemplate, placement.sharedBondId);
        return;
      }
      if (tool === 'charge-apply') {
        const radius =
          (usesTouchTarget(event.pointerType) ? TOUCH_SELECTION_DISTANCE_PX : MOUSE_SELECTION_DISTANCE_PX) /
          viewportRef.current.zoom;
        const hit = selectionAtPoint(
          normalizedToPixel(normalizedPointForEvent(event), sizeRef.current),
          atoms,
          linesRef.current,
          sizeRef.current,
          radius
        );
        if (hit?.kind === 'atom' && !atoms.find((atom) => atom.id === hit.id)?.labelOverride) {
          onAtomChargeApply?.(hit.id);
        }
        return;
      }
      if (tool.startsWith('element-')) {
        const atomicNumber = Number(tool.slice('element-'.length));
        const normalizedPoint = normalizedPointForEvent(event);
        const worldMaximumDistance =
          (usesTouchTarget(event.pointerType) ? TOUCH_SELECTION_DISTANCE_PX : MOUSE_SELECTION_DISTANCE_PX) /
          viewportRef.current.zoom;
        const hit = selectionAtPoint(
          normalizedToPixel(normalizedPoint, sizeRef.current),
          atoms,
          linesRef.current,
          sizeRef.current,
          worldMaximumDistance
        );
        // On an existing atom -> retype it (#255). On empty paper -> place a lone atom, i.e. a
        // new molecule (UI action rules Rule 4.4). On a bond -> ignored, as before.
        if (hit?.kind === 'atom') onAtomElementChange(hit.id, atomicNumber);
        else if (!hit) onAtomPlace(snapPoint(normalizedPoint, event), atomicNumber);
        return;
      }
      if (tool.startsWith('group-')) {
        const normalizedPoint = normalizedPointForEvent(event);
        const worldMaximumDistance =
          (usesTouchTarget(event.pointerType) ? TOUCH_SELECTION_DISTANCE_PX : MOUSE_SELECTION_DISTANCE_PX) /
          viewportRef.current.zoom;
        const hit = selectionAtPoint(
          normalizedToPixel(normalizedPoint, sizeRef.current),
          atoms,
          linesRef.current,
          sizeRef.current,
          worldMaximumDistance
        );
        // #736: on an atom -> the group there (replacing a terminal atom, else bonded on); on empty
        // paper -> the group alone; on a bond -> ignored, as with an element.
        const key = tool.slice('group-'.length);
        if (hit?.kind === 'atom') onAtomGroup?.(hit.id, key);
        else if (!hit) onPaperGroup?.(snapPoint(normalizedPoint, event), key);
        return;
      }
      if (tool.startsWith('r-group-')) {
        const worldMaximumDistance =
          (usesTouchTarget(event.pointerType) ? TOUCH_SELECTION_DISTANCE_PX : MOUSE_SELECTION_DISTANCE_PX) /
          viewportRef.current.zoom;
        const normalizedPoint = normalizedPointForEvent(event);
        const hit = selectionAtPoint(
          normalizedToPixel(normalizedPoint, sizeRef.current),
          atoms,
          linesRef.current,
          sizeRef.current,
          worldMaximumDistance
        );
        // #648: an atom gets an R label; whether it qualifies (terminal, not locked) is the Markush
        // layer's call. #650: a click on paper (not on an atom or bond) goes to the ring-interior
        // attachment point, which ignores clicks outside every ring. Bonds are ignored.
        const label = tool.slice('r-group-'.length);
        if (hit?.kind === 'atom') onAtomRGroupLabel?.(hit.id, label);
        else if (!hit) onPaperRGroupLabel?.(normalizedPoint, label);
        return;
      }
      const atomList = atomListFromTool(tool);
      if (atomList) {
        const worldMaximumDistance =
          (usesTouchTarget(event.pointerType) ? TOUCH_SELECTION_DISTANCE_PX : MOUSE_SELECTION_DISTANCE_PX) /
          viewportRef.current.zoom;
        const hit = selectionAtPoint(
          normalizedToPixel(normalizedPointForEvent(event), sizeRef.current),
          atoms,
          linesRef.current,
          sizeRef.current,
          worldMaximumDistance
        );
        // #654: an atom (any bond count) becomes a variable heteroatom; whether it qualifies is the
        // Markush layer's call. Paper and bonds are ignored.
        if (hit?.kind === 'atom') onAtomAtomList?.(hit.id, atomList);
        return;
      }
      if (tool.startsWith('shape-')) {
        const start = normalizedPointForEvent(event);
        setShapeDrag({ pointerId: event.pointerId, start, current: start });
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
      if (tool === 'select') {
        const pointer = normalizedToPixel(normalizedPointForEvent(event), sizeRef.current);
        const maximumDistance = usesTouchTarget(event.pointerType)
          ? TOUCH_SELECTION_DISTANCE_PX
          : MOUSE_SELECTION_DISTANCE_PX;
        const worldMaximumDistance = maximumDistance / viewportRef.current.zoom;
        const hydrogenAtom = atoms.find((atom) => {
          if (!atom.implicitHydrogenDirection || atom.implicitHydrogenCount === 0) return false;
          const center = normalizedToPixel(atom.position, sizeRef.current);
          // #977: where the H label is drawn. Labels scale with zoom (#385), so this is in paper
          // units like the pointer, not divided by the zoom.
          const hydrogen = implicitHydrogenPosition(
            atom,
            center.x,
            center.y,
            atom.implicitHydrogenDirection,
            LABEL_FONT_SIZE_PX
          );
          return (
            distanceBetween(pointer, hydrogen) <= worldMaximumDistance &&
            distanceBetween(pointer, hydrogen) < distanceBetween(pointer, center)
          );
        });
        if (hydrogenAtom) {
          onSelectionChange({ atomIds: [hydrogenAtom.id], bondIds: [] });
          hydrogenDragRef.current = {
            pointerId: event.pointerId,
            atomId: hydrogenAtom.id,
            direction: hydrogenAtom.implicitHydrogenDirection!
          };
          event.currentTarget.setPointerCapture(event.pointerId);
          return;
        }
        const nextSelection = selectionAtPoint(pointer, atoms, linesRef.current, sizeRef.current, worldMaximumDistance);
        if (!nextSelection) {
          // ADR 0014 -- a reaction box is a container: pressing inside it (off any atom or bond)
          // drags the box with everything in it; an empty box moves on its own.
          const zone =
            event.shiftKey || !onReactionBoxMove
              ? undefined
              : reactionZones.find(
                  ({ rect }) =>
                    pointer.x >= rect.minX && pointer.x <= rect.maxX && pointer.y >= rect.minY && pointer.y <= rect.maxY
                );
          if (zone) {
            const ids = new Set(zone.atomIds);
            onSelectionChange({ atomIds: [...ids], bondIds: bondIdsForSelectedAtoms(atoms, linesRef.current, ids) });
            const positions = Object.fromEntries(
              atoms.filter((candidate) => ids.has(candidate.id)).map((candidate) => [candidate.id, candidate.position])
            );
            setAtomDrag({
              pointerId: event.pointerId,
              start: normalizedPointForEvent(event),
              initialPositions: positions,
              currentPositions: positions,
              moved: false,
              reactionZone: zone.zone,
              offset: { x: 0, y: 0 }
            });
            event.currentTarget.setPointerCapture(event.pointerId);
            return;
          }
          const start = normalizedPointForEvent(event);
          if (lassoModeActive) {
            setLassoDrag({ pointerId: event.pointerId, points: [start], additive: event.shiftKey });
          } else {
            setMarqueeDrag({ pointerId: event.pointerId, start, current: start, additive: event.shiftKey });
          }
          if (!event.shiftKey) onSelectionChange({ atomIds: [], bondIds: [] });
          event.currentTarget.setPointerCapture(event.pointerId);
          return;
        }
        // ADR 0014 -- progressive selection inside a reaction box: pressing an atom of a boxed
        // molecule selects (and drags) the whole molecule; pressing it again narrows on release.
        const molecule =
          nextSelection.kind === 'atom' && !event.shiftKey ? boxedMolecule?.(nextSelection.id) : undefined;
        if (molecule) {
          const whole = molecule.every((id) => selection.atomIds.includes(id));
          const dragged = whole ? selection.atomIds : molecule;
          if (!whole) {
            const inMolecule = new Set(molecule);
            onSelectionChange({
              atomIds: [...molecule],
              bondIds: bondIdsForSelectedAtoms(atoms, linesRef.current, inMolecule)
            });
          }
          const positions = Object.fromEntries(
            atoms
              .filter((candidate) => dragged.includes(candidate.id))
              .map((candidate) => [candidate.id, candidate.position])
          );
          setAtomDrag({
            pointerId: event.pointerId,
            start: normalizedPointForEvent(event),
            initialPositions: positions,
            currentPositions: positions,
            moved: false,
            ...(whole ? { narrowTo: { atomIds: [nextSelection.id], bondIds: [] } } : {})
          });
          event.currentTarget.setPointerCapture(event.pointerId);
          return;
        }
        const key = nextSelection.kind === 'atom' ? 'atomIds' : 'bondIds';
        const alreadySelected = selection[key].includes(nextSelection.id);
        const next = event.shiftKey
          ? {
              atomIds:
                nextSelection.kind === 'atom'
                  ? alreadySelected
                    ? selection.atomIds.filter((id) => id !== nextSelection.id)
                    : [...selection.atomIds, nextSelection.id]
                  : selection.atomIds,
              bondIds:
                nextSelection.kind === 'bond'
                  ? alreadySelected
                    ? selection.bondIds.filter((id) => id !== nextSelection.id)
                    : [...selection.bondIds, nextSelection.id]
                  : selection.bondIds
            }
          : alreadySelected
            ? selection
            : {
                atomIds: nextSelection.kind === 'atom' ? [nextSelection.id] : [],
                bondIds: nextSelection.kind === 'bond' ? [nextSelection.id] : []
              };
        onSelectionChange(next);
        if (nextSelection.kind === 'atom' && next.atomIds.includes(nextSelection.id)) {
          const atom = atoms.find((candidate) => candidate.id === nextSelection.id);
          if (atom) {
            const draggedIds = alreadySelected ? next.atomIds : [atom.id];
            setAtomDrag({
              pointerId: event.pointerId,
              start: normalizedPointForEvent(event),
              initialPositions: Object.fromEntries(
                atoms
                  .filter((candidate) => draggedIds.includes(candidate.id))
                  .map((candidate) => [candidate.id, candidate.position])
              ),
              currentPositions: Object.fromEntries(
                atoms
                  .filter((candidate) => draggedIds.includes(candidate.id))
                  .map((candidate) => [candidate.id, candidate.position])
              ),
              moved: false
            });
            event.currentTarget.setPointerCapture(event.pointerId);
          }
        }
        return;
      }
      // UI action rules Rule 3: with a bond tool armed, a pointer-down that lands on an existing
      // bond nearer its midpoint than either endpoint retypes that bond instead of starting a
      // draft. Nearer an endpoint falls through to the normal "start a bond from this node".
      {
        const pointerPixel = normalizedToPixel(normalizedPointForEvent(event), sizeRef.current);
        const worldMaximumDistance =
          (usesTouchTarget(event.pointerType) ? TOUCH_SELECTION_DISTANCE_PX : MOUSE_SELECTION_DISTANCE_PX) /
          viewportRef.current.zoom;
        const hit = selectionAtPoint(pointerPixel, atoms, linesRef.current, sizeRef.current, worldMaximumDistance);
        if (hit?.kind === 'bond') {
          const line = linesRef.current.find((candidate) => candidate.id === hit.id);
          if (line) {
            const startPixel = normalizedToPixel(line.start, sizeRef.current);
            const endPixel = normalizedToPixel(line.end, sizeRef.current);
            const midpointPixel = { x: (startPixel.x + endPixel.x) / 2, y: (startPixel.y + endPixel.y) / 2 };
            const toMidpoint = distanceBetween(pointerPixel, midpointPixel);
            if (
              toMidpoint <= distanceBetween(pointerPixel, startPixel) &&
              toMidpoint <= distanceBetween(pointerPixel, endPixel)
            ) {
              onBondToolApply(hit.id);
              return;
            }
          }
        }
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      const pointerPoint = normalizedPointForEvent(event);
      const pointerPixel = normalizedToPixel(pointerPoint, sizeRef.current);
      const offerDistance = usesTouchTarget(event.pointerType)
        ? bondConfig.touchOfferDistancePx
        : bondConfig.existingEndpointOfferDistancePx;
      const existing = nearestPoint(
        pointerPixel,
        uniqueEndpoints(linesRef.current, atoms, sizeRef.current),
        offerDistance / viewportRef.current.zoom
      );
      const start = existing ? pixelToNormalized(existing, sizeRef.current) : snapPoint(pointerPoint, event);
      activeDraftsRef.current.set(event.pointerId, {
        pointerId: event.pointerId,
        pointerType: toSupportedPointerType(event.pointerType),
        pointerStart: pointerPoint,
        pointerCurrent: pointerPoint,
        start,
        end: start,
        moved: false,
        startedFromExistingEndpoint: Boolean(existing)
      });
      setHoveredEndpoint(null);
      syncDraftState();
    },
    [
      snapPoint,
      bondConfig.existingEndpointOfferDistancePx,
      bondConfig.touchOfferDistancePx,
      atoms,
      normalizedPointForEvent,
      onSelectionChange,
      onAtomElementChange,
      onAtomChargeApply,
      onAtomPlace,
      onAtomRGroupLabel,
      onPaperRGroupLabel,
      onAtomGroup,
      onPaperGroup,
      onAtomAtomList,
      repeatUnitLabelAt,
      onRepeatUnitActivate,
      onBondToolApply,
      selection,
      syncDraftState,
      tool,
      ringTemplate,
      onRingPlace,
      onShapePlace,
      bondConfig.lengthPx,
      beginPan,
      lassoModeActive,
      reactionZones,
      onReactionBoxMove,
      boxedMolecule,
      sizeRef,
      linesRef,
      viewportRef.current.zoom
    ]
  );

  // #637 -- box/lasso also select annotations, each only when fully enclosed (accepted S2).
  const enclosedAnnotationIds = useCallback(
    (inside: (point: NormalizedPoint) => boolean): string[] =>
      annotations
        .filter((annotation) => annotationExtentPoints(annotation, sizeRef.current, measureText).every(inside))
        .map((annotation) => annotation.id),
    [annotations, measureText, sizeRef]
  );
  // Shift adds to the current selection, annotations included.
  const regionSelection = useCallback(
    (
      additive: boolean,
      atomIds: readonly string[],
      bondIds: readonly string[],
      annotationIds: readonly string[]
    ): EditorSelection =>
      additive
        ? {
            atomIds: [...new Set([...selection.atomIds, ...atomIds])],
            bondIds: [...new Set([...selection.bondIds, ...bondIds])],
            annotationIds: [...new Set([...(selection.annotationIds ?? []), ...annotationIds])]
          }
        : {
            atomIds: [...atomIds],
            bondIds: [...bondIds],
            ...(annotationIds.length ? { annotationIds: [...annotationIds] } : {})
          },
    [selection]
  );

  // ADR 0022 (as amended) -- the salt's cation-anion pair whose line between the ions is within
  // half a touch target (44 px) of the pointer, so its bolt shows; null when none is.
  const ionicBondNear = useCallback(
    (pointer: PixelPoint): string | null => {
      if (!ionicPairs?.length) return null;
      const reach = 22 / viewportRef.current.zoom;
      const positionOf = (id: string) => atoms.find((atom) => atom.id === id)?.position;
      let nearest: { id: string; distance: number } | null = null;
      for (const pair of ionicPairs) {
        const [cation, anion] = [positionOf(pair.cationId), positionOf(pair.anionId)];
        if (!cation || !anion) continue;
        const distance = distanceToSegment(
          pointer,
          normalizedToPixel(cation, sizeRef.current),
          normalizedToPixel(anion, sizeRef.current)
        );
        if (distance <= reach && (!nearest || distance < nearest.distance)) nearest = { id: pair.id, distance };
      }
      return nearest?.id ?? null;
    },
    [atoms, ionicPairs, sizeRef, viewportRef]
  );

  const atomNear = useCallback(
    (pointer: PixelPoint): string | null => {
      const reach = 16 / viewportRef.current.zoom;
      let nearest: { id: string; distance: number } | null = null;
      for (const atom of atoms) {
        const distance = distanceBetween(pointer, normalizedToPixel(atom.position, sizeRef.current));
        if (distance <= reach && (!nearest || distance < nearest.distance)) nearest = { id: atom.id, distance };
      }
      return nearest?.id ?? null;
    },
    [atoms, sizeRef, viewportRef]
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      // ADR 0022 D2 -- a mouse pointing at an ionic bond shows its bolt, in every mode (owner test:
      // Select mode returned before the hover check). Touch has no hover; selecting shows it there.
      if (event.pointerType === 'mouse') {
        const pointer = normalizedToPixel(normalizedPointForEvent(event), sizeRef.current);
        setHoveredIonicBondId(ionicBondNear(pointer));
        setHoveredAtomId(atomNear(pointer));
      }
      if (panDrag?.pointerId === event.pointerId) {
        event.preventDefault();
        updatePan(panDrag, event.clientX, event.clientY);
        return;
      }
      if (ringTemplate) {
        setRingPointer(normalizedPointForEvent(event));
        return;
      }
      if (shapeDrag?.pointerId === event.pointerId) {
        event.preventDefault();
        setShapeDrag({ ...shapeDrag, current: normalizedPointForEvent(event) });
        return;
      }
      if (tool === 'select') {
        if (atomDrag?.pointerId === event.pointerId) {
          event.preventDefault();
          const currentPosition = normalizedPointForEvent(event);
          const moved =
            atomDrag.moved ||
            distanceBetween(
              normalizedToPixel(atomDrag.start, sizeRef.current),
              normalizedToPixel(currentPosition, sizeRef.current)
            ) >=
              CLICK_MOVEMENT_TOLERANCE_PX / viewportRef.current.zoom;
          // #772: objects may extend beyond the page and the visible paper -- no clamping. With
          // Snap to grid, the atom nearest the press lands on the grid and the rest follow.
          const { x: dx, y: dy } = snappedDragOffset(
            grabbedPoint(atomDrag.start, atomDrag.initialPositions),
            { x: currentPosition.x - atomDrag.start.x, y: currentPosition.y - atomDrag.start.y },
            snapPoint,
            { altKey: event.altKey, exclude: new Set(Object.keys(atomDrag.initialPositions)) }
          );
          setAtomDrag({
            ...atomDrag,
            currentPositions: Object.fromEntries(
              Object.entries(atomDrag.initialPositions).map(([id, point]) => [id, { x: point.x + dx, y: point.y + dy }])
            ),
            moved,
            ...(atomDrag.reactionZone ? { offset: { x: dx, y: dy } } : {})
          });
          return;
        }
        if (marqueeDrag?.pointerId === event.pointerId) {
          event.preventDefault();
          setMarqueeDrag({ ...marqueeDrag, current: normalizedPointForEvent(event) });
          return;
        }
        if (lassoDrag?.pointerId === event.pointerId) {
          event.preventDefault();
          const point = normalizedPointForEvent(event);
          const last = lassoDrag.points.at(-1)!;
          // Only record a point once the pointer has actually moved a few pixels -- otherwise a
          // slow, careful freehand drag accumulates a huge point array for no visual benefit.
          if (
            distanceBetween(normalizedToPixel(last, sizeRef.current), normalizedToPixel(point, sizeRef.current)) >=
            CLICK_MOVEMENT_TOLERANCE_PX / viewportRef.current.zoom
          ) {
            setLassoDrag({ ...lassoDrag, points: [...lassoDrag.points, point] });
          }
          return;
        }
        const drag = hydrogenDragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        event.preventDefault();
        const atom = atoms.find((candidate) => candidate.id === drag.atomId);
        if (!atom) return;
        const atomCenter = normalizedToPixel(atom.position, sizeRef.current);
        const pointer = normalizedToPixel(normalizedPointForEvent(event), sizeRef.current);
        const length = distanceBetween(atomCenter, pointer);
        if (length < CLICK_MOVEMENT_TOLERANCE_PX / viewportRef.current.zoom) return;
        const direction = {
          x: (pointer.x - atomCenter.x) / length,
          y: (pointer.y - atomCenter.y) / length
        };
        hydrogenDragRef.current = { ...drag, direction };
        setHydrogenPreviewDirections((current) => ({ ...current, [atom.id]: direction }));
        return;
      }
      const pointerPoint = normalizedPointForEvent(event);
      const pointerPixel = normalizedToPixel(pointerPoint, sizeRef.current);
      const current = activeDraftsRef.current.get(event.pointerId);
      if (!current) {
        setHoveredEndpoint(
          nearestPoint(
            pointerPixel,
            uniqueEndpoints(linesRef.current, atoms, sizeRef.current),
            bondConfig.existingEndpointOfferDistancePx
          ) ?? null
        );
        return;
      }
      event.preventDefault();
      const startPixel = normalizedToPixel(current.start, sizeRef.current);
      const snapDistance = usesTouchTarget(current.pointerType)
        ? bondConfig.touchTargetRadiusPx
        : bondConfig.existingEndpointSnapDistancePx;
      const moved =
        current.moved ||
        distanceBetween(normalizedToPixel(current.pointerStart, sizeRef.current), pointerPixel) >=
          CLICK_MOVEMENT_TOLERANCE_PX / viewportRef.current.zoom;
      const existingSnap = nearestPoint(
        pointerPixel,
        uniqueEndpoints(linesRef.current, atoms, sizeRef.current).filter(
          (point) => distanceBetween(point, startPixel) >= 0.5
        ),
        snapDistance / viewportRef.current.zoom
      );
      const proposalSnap = nearestPoint(
        pointerPixel,
        proposedBondEndpoints(startPixel, bondConfig),
        usesTouchTarget(current.pointerType)
          ? bondConfig.touchTargetRadiusPx / viewportRef.current.zoom
          : bondConfig.proposalSnapDistancePx / viewportRef.current.zoom
      );
      const snappedPixel = existingSnap ?? proposalSnap;
      activeDraftsRef.current.set(event.pointerId, {
        ...current,
        moved,
        pointerCurrent: pointerPoint,
        end: snappedPixel ? pixelToNormalized(snappedPixel, sizeRef.current) : snapPoint(pointerPoint, event)
      });
      syncDraftState();
    },
    [
      snapPoint,
      atomDrag,
      atoms,
      bondConfig,
      shapeDrag,
      marqueeDrag,
      lassoDrag,
      normalizedPointForEvent,
      panDrag,
      updatePan,
      ringTemplate,
      syncDraftState,
      tool,
      linesRef,
      sizeRef,
      ionicBondNear,
      atomNear,
      viewportRef.current.zoom
    ]
  );

  const finishPointer = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, cancelled: boolean) => {
      if (panDrag?.pointerId === event.pointerId) {
        endPan();
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        return;
      }
      if (atomDrag?.pointerId === event.pointerId) {
        setAtomDrag(null);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!cancelled && !atomDrag.moved && atomDrag.narrowTo) {
          onSelectionChange(atomDrag.narrowTo);
        }
        if (!cancelled && atomDrag.moved) {
          if (atomDrag.reactionZone && atomDrag.offset && onReactionBoxMove) {
            onReactionBoxMove(atomDrag.reactionZone, atomDrag.offset);
          } else {
            onAtomsMove(atomDrag.currentPositions);
          }
        }
        return;
      }
      if (shapeDrag?.pointerId === event.pointerId) {
        setShapeDrag(null);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!cancelled && tool.startsWith('shape-')) {
          onShapePlace(
            tool.slice('shape-'.length) as 'ellipse' | 'rect' | 'triangle' | 'star',
            shapeDrag.start,
            shapeDrag.current
          );
        }
        return;
      }
      if (marqueeDrag?.pointerId === event.pointerId) {
        setMarqueeDrag(null);
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          event.currentTarget.releasePointerCapture(event.pointerId);
        if (!cancelled) {
          const minX = Math.min(marqueeDrag.start.x, marqueeDrag.current.x);
          const maxX = Math.max(marqueeDrag.start.x, marqueeDrag.current.x);
          const minY = Math.min(marqueeDrag.start.y, marqueeDrag.current.y);
          const maxY = Math.max(marqueeDrag.start.y, marqueeDrag.current.y);
          const atomIds = atoms
            .filter(
              (atom) =>
                atom.position.x >= minX && atom.position.x <= maxX && atom.position.y >= minY && atom.position.y <= maxY
            )
            .map((atom) => atom.id);
          const bondIds = bondIdsForSelectedAtoms(atoms, lines, new Set(atomIds));
          const annotationIds = enclosedAnnotationIds(
            (point) => point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY
          );
          onSelectionChange(regionSelection(marqueeDrag.additive, atomIds, bondIds, annotationIds));
        }
        return;
      }
      if (lassoDrag?.pointerId === event.pointerId) {
        setLassoDrag(null);
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          event.currentTarget.releasePointerCapture(event.pointerId);
        if (!cancelled) {
          const atomIds = atoms
            .filter((atom) => pointInPolygon(atom.position, lassoDrag.points))
            .map((atom) => atom.id);
          const bondIds = bondIdsForSelectedAtoms(atoms, lines, new Set(atomIds));
          const annotationIds = enclosedAnnotationIds((point) => pointInPolygon(point, lassoDrag.points));
          onSelectionChange(regionSelection(lassoDrag.additive, atomIds, bondIds, annotationIds));
        }
        return;
      }
      if (hydrogenDragRef.current?.pointerId === event.pointerId) {
        const drag = hydrogenDragRef.current;
        hydrogenDragRef.current = null;
        setHydrogenPreviewDirections((current) => {
          const { [drag.atomId]: _removed, ...remaining } = current;
          return remaining;
        });
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!cancelled) onImplicitHydrogenDirectionChange(drag.atomId, drag.direction);
        return;
      }
      const draft = activeDraftsRef.current.get(event.pointerId);
      if (!draft) return;
      event.preventDefault();
      activeDraftsRef.current.delete(event.pointerId);
      syncDraftState();
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (cancelled) return;

      if (!draft.moved) {
        const angle = draft.startedFromExistingEndpoint
          ? connectedBondAngle(
              draft.start,
              linesRef.current,
              sizeRef.current,
              bondConfig.connectedBondAngleDegrees,
              bondOrders,
              armedBondOrder,
              bondConfig.lengthPx
            )
          : bondConfig.defaultAngleDegrees;
        const endPixel = pointAtAngle(normalizedToPixel(draft.start, sizeRef.current), bondConfig.lengthPx, angle);
        commitDraft({ ...draft, end: pixelToNormalized(endPixel, sizeRef.current) }, true);
        return;
      }

      const pointerPoint = normalizedPointForEvent(event);
      const pointerPixel = normalizedToPixel(pointerPoint, sizeRef.current);
      const startPixel = normalizedToPixel(draft.start, sizeRef.current);
      const snapDistance = usesTouchTarget(draft.pointerType)
        ? bondConfig.touchTargetRadiusPx
        : bondConfig.existingEndpointSnapDistancePx;
      const existingSnap = nearestPoint(
        pointerPixel,
        uniqueEndpoints(linesRef.current, atoms, sizeRef.current).filter(
          (point) => distanceBetween(point, startPixel) >= 0.5
        ),
        snapDistance / viewportRef.current.zoom
      );
      const proposalSnap = nearestPoint(
        pointerPixel,
        proposedBondEndpoints(startPixel, bondConfig),
        usesTouchTarget(draft.pointerType)
          ? bondConfig.touchTargetRadiusPx / viewportRef.current.zoom
          : bondConfig.proposalSnapDistancePx / viewportRef.current.zoom
      );
      const snappedPixel = existingSnap ?? proposalSnap;
      const end = snappedPixel ? pixelToNormalized(snappedPixel, sizeRef.current) : snapPoint(pointerPoint, event);
      if (
        distanceBetween(startPixel, normalizedToPixel(end, sizeRef.current)) <
        CLICK_MOVEMENT_TOLERANCE_PX / viewportRef.current.zoom
      ) {
        return;
      }
      commitDraft({ ...draft, end }, Boolean(snappedPixel));
    },
    [
      snapPoint,
      atomDrag,
      atoms,
      bondConfig,
      shapeDrag,
      commitDraft,
      lines,
      marqueeDrag,
      lassoDrag,
      normalizedPointForEvent,
      onAtomsMove,
      onReactionBoxMove,
      onShapePlace,
      onImplicitHydrogenDirectionChange,
      onSelectionChange,
      panDrag,
      endPan,
      selection,
      syncDraftState,
      tool,
      bondOrders,
      armedBondOrder,
      linesRef,
      sizeRef,
      viewportRef.current.zoom,
      enclosedAnnotationIds,
      regionSelection
    ]
  );

  const handlePointerLeave = useCallback(() => {
    setRingPointer(null);
    setHoveredEndpoint(null);
    setHoveredIonicBondId(null);
    setHoveredAtomId(null);
  }, []);

  const hasActiveDraft = useCallback((pointerId: number) => activeDraftsRef.current.has(pointerId), []);

  return {
    activeDrafts,
    hoveredEndpoint,
    hoveredIonicBondId,
    hoveredAtomId,
    hydrogenPreviewDirections,
    atomDrag,
    ringPointer,
    shapeDrag,
    marqueeDrag,
    lassoDrag,
    handlePointerDown,
    handlePointerMove,
    finishPointer,
    handlePointerLeave,
    hasActiveDraft,
    cancel
  };
}
